import React, { useEffect, useState } from 'react';
import { db } from '../../firebase';
import {
  doc,
  getDoc,
  getDocs,
  collection,
  updateDoc,
  Timestamp,
  setDoc,
  query,
  where,
  limit,
  orderBy,
} from 'firebase/firestore';
import { useParams } from 'react-router-dom';
import {
  HiOutlineExclamationTriangle,
  HiOutlineCheckCircle,
} from 'react-icons/hi2';
import ClientTimeline from '../Shared/ClientTimeline';

const ClientTracking = () => {
  const { clientId } = useParams();

  const [clientData, setClientData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showArchivedProjects, setShowArchivedProjects] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [resolvedClientId, setResolvedClientId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPhase, setEditingPhase] = useState(null);
  const [phaseForm, setPhaseForm] = useState(null);
  const [showAddPhaseModal, setShowAddPhaseModal] = useState(false);
  const [newPhase, setNewPhase] = useState({
    name: '',
    status: 'en_proceso',
    requiresPayment: false,
    notes: '',
    startDate: '',
    deadline: '',
    subTasks: [],
  });
  const [newSubTask, setNewSubTask] = useState('');
  const [phaseSubTask, setPhaseSubTask] = useState('');
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false);
  const [newProject, setNewProject] = useState({
    projectName: '',
    invoiceId: '',
  });
  const [invoices, setInvoices] = useState([]);
  const [clientTimelineEntries, setClientTimelineEntries] = useState([]);
  const [clientTimelineLoading, setClientTimelineLoading] = useState(false);

  const decodedClientParam = clientId ? decodeURIComponent(clientId) : '';
  const effectiveClientId = resolvedClientId || decodedClientParam;

  const createSubTaskEntry = (title) => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    title,
    done: false,
  });

  const toDateStringValue = (value) => {
    if (!value) return '';
    const date =
      value instanceof Date
        ? value
        : value.toDate
        ? value.toDate()
        : value.seconds
        ? new Date(value.seconds * 1000)
        : new Date(value);
    return isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 16);
  };

  const normalizeDateValue = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    if (value.toDate) return value.toDate();
    if (value.seconds) return new Date(value.seconds * 1000);
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? null : parsed;
  };

  const handleNewPhaseSubTaskAdd = () => {
    const title = newSubTask.trim();
    if (!title) return;
    setNewPhase((prev) => ({
      ...prev,
      subTasks: [...(prev.subTasks || []), createSubTaskEntry(title)],
    }));
    setNewSubTask('');
  };

  const handleNewPhaseSubTaskToggle = (index) => {
    setNewPhase((prev) => {
      const subTasks = [...(prev.subTasks || [])];
      subTasks[index] = { ...subTasks[index], done: !subTasks[index].done };
      return { ...prev, subTasks };
    });
  };

  const handleNewPhaseSubTaskRemove = (index) => {
    setNewPhase((prev) => {
      const subTasks = [...(prev.subTasks || [])];
      subTasks.splice(index, 1);
      return { ...prev, subTasks };
    });
  };

  const openPhaseModal = (index, phase) => {
    setEditingPhase(index);
    setPhaseForm({
      name: phase.name || '',
      status: phase.status || 'pendiente',
      notes: phase.notes || '',
      requiresPayment: !!phase.requiresPayment,
      startDate: toDateStringValue(phase.startDate),
      deadline: toDateStringValue(phase.deadline),
      subTasks: (phase.subTasks || []).map((task) => ({
        id: task.id || createSubTaskEntry(task.title || '').id,
        title: task.title || '',
        done: !!task.done,
      })),
    });
    setPhaseSubTask('');
    setShowModal(true);
  };

  const handlePhaseSubTaskAdd = () => {
    const title = phaseSubTask.trim();
    if (!title || !phaseForm) return;
    setPhaseForm((prev) => ({
      ...prev,
      subTasks: [...(prev.subTasks || []), createSubTaskEntry(title)],
    }));
    setPhaseSubTask('');
  };

  const handlePhaseSubTaskToggle = (index) => {
    setPhaseForm((prev) => {
      const subTasks = [...(prev?.subTasks || [])];
      subTasks[index] = { ...subTasks[index], done: !subTasks[index].done };
      return { ...prev, subTasks };
    });
  };

  const handlePhaseSubTaskTitle = (index, title) => {
    setPhaseForm((prev) => {
      const subTasks = [...(prev?.subTasks || [])];
      subTasks[index] = { ...subTasks[index], title };
      return { ...prev, subTasks };
    });
  };

  const handlePhaseSubTaskRemove = (index) => {
    setPhaseForm((prev) => {
      const subTasks = [...(prev?.subTasks || [])];
      subTasks.splice(index, 1);
      return { ...prev, subTasks };
    });
  };

  const resolveClientRecord = async (rawId) => {
    const candidateId = rawId ? decodeURIComponent(rawId) : '';
    if (!candidateId) return null;

    const directRef = doc(db, 'clients', candidateId);
    const directSnap = await getDoc(directRef);
    if (directSnap.exists()) {
      return { id: directRef.id, data: directSnap.data() };
    }

    const emailCandidate = candidateId.includes('@') ? candidateId.toLowerCase() : null;
    const searchConfigs = [];

    if (emailCandidate) {
      searchConfigs.push(where('email', '==', emailCandidate));
    }
    searchConfigs.push(where('uid', '==', candidateId));

    for (const condition of searchConfigs) {
      const q = query(collection(db, 'clients'), condition, limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const docSnap = snap.docs[0];
        return { id: docSnap.id, data: docSnap.data() };
      }
    }

    return null;
  };

  const fetchClientTimeline = async (clientDocId) => {
    if (!clientDocId) return;
    setClientTimelineLoading(true);
    try {
      const q = query(
        collection(db, 'clients', clientDocId, 'timeline'),
        orderBy('createdAt', 'desc'),
        limit(50)
      );
      const snap = await getDocs(q);
      setClientTimelineEntries(snap.docs.map((docSnap) => ({ id: docSnap.id, ...docSnap.data() })));
    } catch (err) {
      console.error('❌ Error cargando timeline de cliente:', err);
    } finally {
      setClientTimelineLoading(false);
    }
  };

  const fetchClient = async () => {
    try {
      console.log('📡 Ejecutando fetchClient con clientId:', clientId);
      const resolved = await resolveClientRecord(clientId);

      if (!resolved) {
        console.warn('⚠️ Cliente no encontrado');
        setClientData(null);
        setResolvedClientId(null);
        return;
      }
      const { id: clientDocId, data: client } = resolved;
      setResolvedClientId(clientDocId);
  
      const clientRef = doc(db, 'clients', clientDocId);
      const clientSnap = await getDoc(clientRef);
      const clientDataFromDoc = clientSnap.exists() ? clientSnap.data() : client;
  
      const [invoicesSnap, projectsSnap] = await Promise.all([
        getDocs(collection(clientRef, 'invoices')),
        getDocs(collection(clientRef, 'projects')),
      ]);
  
      const invoiceList = invoicesSnap.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      console.log('🧾 Invoices encontradas:', invoiceList);
  
      // 🔎 Obtener proyectos
      const projectList = projectsSnap.docs.map((doc) => {
        const project = doc.data();
      
        const enrichedPhases = (project.projectPhases || []).map((phase) => {
          if (!phase.requiresPayment || !project.invoiceId) return phase;
      
          const invoice = invoiceList.find((inv) => inv.id === project.invoiceId);
          const isPaid =
            invoice?.status === 'pagado' ||
            invoice?.payments?.some((p) => parseFloat(p.amount) > 0);
      
          return {
            ...phase,
            paymentDone: !!isPaid,
            invoiceAmount: invoice?.total || 0,
            invoiceStatus: invoice?.status || 'pendiente',
          };
        });
      
        return {
          id: doc.id,
          ...project,
          clientId: clientDocId,
          projectPhases: enrichedPhases,
        };
      });
      
  
      console.log('📁 Proyectos encontrados:', projectList);
 
      // ✅ Guardar en estado
      setClientData({ ...clientDataFromDoc, id: clientDocId });
      setInvoices(invoiceList);
      setProjects(projectList);
      fetchClientTimeline(clientDocId);
  
      setSelectedProject((prev) => {
        if (prev) {
          const keep = projectList.find((p) => p.id === prev.id);
          if (keep) return keep;
        }
        const list = showArchivedProjects ? projectList : projectList.filter((p) => p.estado !== 'finalizado');
        return list[0] || projectList[0] || null;
      });
    } catch (error) {
      console.error('❌ Error en fetchClient:', error);
    }
  };
  

  useEffect(() => {
    setResolvedClientId(null);
  }, [clientId]);

  useEffect(() => {
    if (clientId) {
      console.log('🌀 useEffect ejecutado con clientId:', clientId);
      fetchClient();
    }
  }, [clientId, showArchivedProjects]);

  useEffect(() => {
    if (!selectedProject || !projects.length) return;
    const isVisible = showArchivedProjects || selectedProject.estado !== 'finalizado';
    if (!isVisible) {
      const next = projects.find((p) => p.estado !== 'finalizado');
      setSelectedProject(next || projects[0] || null);
      setShowHistory(false);
    }
  }, [showArchivedProjects, projects, selectedProject]);
  

  const handleSavePhase = async () => {
    if (editingPhase === null || !phaseForm || !selectedProject) return;
    if (!effectiveClientId) return;
    const clientRef = doc(db, 'clients', effectiveClientId);
    const projectRef = doc(clientRef, 'projects', selectedProject.id);

    const updatedPhases = [...(selectedProject.projectPhases || [])];
    const current = { ...updatedPhases[editingPhase] };

    current.name = phaseForm.name || current.name;
    current.status = phaseForm.status || current.status || 'pendiente';
    current.notes = phaseForm.notes || '';
    current.requiresPayment = !!phaseForm.requiresPayment;
    current.startDate = phaseForm.startDate
      ? Timestamp.fromDate(new Date(phaseForm.startDate))
      : null;
    current.deadline = phaseForm.deadline
      ? Timestamp.fromDate(new Date(phaseForm.deadline))
      : null;
    current.subTasks = (phaseForm.subTasks || []).map((task) => ({
      id: task.id || createSubTaskEntry(task.title || '').id,
      title: task.title || '',
      done: !!task.done,
    }));

    updatedPhases[editingPhase] = current;

    const updatedTimeline = [
      ...(selectedProject.projectTimeline || []),
      {
        title: `✏️ Fase actualizada: ${current.name || 'Sin título'}`,
        description: `Estado: ${current.status}. ${
          current.notes ? `Notas: ${current.notes}` : ''
        }`.trim(),
        color: 'purple',
        timestamp: Timestamp.now(),
      },
    ];

    await updateDoc(projectRef, {
      projectPhases: updatedPhases,
      projectTimeline: updatedTimeline,
    });

    setShowModal(false);
    setPhaseForm(null);
    setEditingPhase(null);
    setPhaseSubTask('');
    fetchClient();
  };

  const handleAddPhase = async () => {
    if (!selectedProject || !effectiveClientId) return;
    const clientRef = doc(db, 'clients', effectiveClientId);
    const projectRef = doc(clientRef, 'projects', selectedProject.id);

    const freshPhase = {
      ...newPhase,
      startDate: newPhase.startDate ? Timestamp.fromDate(new Date(newPhase.startDate)) : null,
      deadline: newPhase.deadline ? Timestamp.fromDate(new Date(newPhase.deadline)) : null,
      subTasks: (newPhase.subTasks || []).map((task) => ({
        id: task.id || createSubTaskEntry(task.title || '').id,
        title: task.title || '',
        done: !!task.done,
      })),
    };

    const updatedPhases = [...(selectedProject.projectPhases || []), freshPhase];
    const updatedTimeline = [
      ...(selectedProject.projectTimeline || []),
      {
        title: `📌 Nueva Fase: ${newPhase.name}`,
        description: newPhase.notes || 'Sin notas',
        color: 'blue',
        timestamp: Timestamp.now(),
      },
    ];

    await updateDoc(projectRef, {
      projectPhases: updatedPhases,
      projectTimeline: updatedTimeline,
    });

    setShowAddPhaseModal(false);
    setNewPhase({
      name: '',
      status: 'en_proceso',
      requiresPayment: false,
      notes: '',
      startDate: '',
      deadline: '',
      subTasks: [],
    });
    setNewSubTask('');
    fetchClient();
  };

  const handleToggleProjectStatus = async (project, finalize = true) => {
    if (!project || !effectiveClientId) return;
    const clientRef = doc(db, 'clients', effectiveClientId);
    const projectRef = doc(clientRef, 'projects', project.id);

    const newStatus = finalize ? 'finalizado' : 'activo';
    const entry = {
      title: finalize ? '✅ Proyecto finalizado' : '🔁 Proyecto reactivado',
      description: finalize
        ? `El proyecto "${project.projectName}" se marcó como finalizado.`
        : `El proyecto "${project.projectName}" vuelve a estado activo.`,
      color: finalize ? 'green' : 'blue',
      timestamp: Timestamp.now(),
    };

    await updateDoc(projectRef, {
      estado: newStatus,
      archivedAt: finalize ? Timestamp.now() : null,
      projectTimeline: [...(project.projectTimeline || []), entry],
    });

    if (finalize) {
      setShowHistory(false);
    }

    fetchClient();
  };


if (!clientData) {
  return <p className="text-gray-500">Cargando cliente...</p>;
}

const activeProjects = projects.filter((p) => p.estado !== 'finalizado');
const projectOptions = showArchivedProjects ? projects : activeProjects;
const isArchivedProject = selectedProject?.estado === 'finalizado';

if (!selectedProject) {
  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div className="bg-white rounded shadow p-4">
        <h2 className="text-xl font-semibold">Este cliente no tiene proyectos aún.</h2>

        <button
          onClick={() => setShowCreateProjectModal(true)}
          className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 mt-3"
        >
          ➕ Crear proyecto desde factura
        </button>
      </div>

      {/* ✅ Modal para crear proyecto */}
      {showCreateProjectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center">
          <div className="bg-white rounded p-6 w-full max-w-md space-y-4 shadow">
            <h3 className="text-lg font-semibold">Nuevo Proyecto</h3>

            {/* Nombre proyecto */}
            <input
              type="text"
              placeholder="Ej: Website nuevo"
              value={newProject.projectName}
              onChange={(e) =>
                setNewProject({ ...newProject, projectName: e.target.value })
              }
              className="w-full border rounded p-2 text-sm"
            />

            {/* Selección de factura */}
            <select
              className="w-full border rounded p-2 text-sm"
              value={newProject.invoiceId}
              onChange={(e) =>
                setNewProject({ ...newProject, invoiceId: e.target.value })
              }
            >
              <option value="">-- Ninguna factura --</option>
              {invoices.map((inv) => (
                <option key={inv.id} value={inv.id}>
                  Factura #{inv.id} — ${inv.total ?? 0}
                </option>
              ))}
            </select>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setShowCreateProjectModal(false)}
                className="px-4 py-2 bg-gray-200 rounded"
              >
                Cancelar
              </button>

              <button
                onClick={async () => {
                  if (!effectiveClientId) return;
                  const clientRef = doc(db, "clients", effectiveClientId);
                  const newProjectRef = doc(collection(clientRef, "projects"));

                  const projectData = {
                    projectName: newProject.projectName || "Proyecto sin nombre",
                    invoiceId: newProject.invoiceId || null,
                    projectPhases: [],
                    projectTimeline: [],
                    estado: "activo",
                    createdAt: Timestamp.now(),
                  };

                  await setDoc(newProjectRef, projectData);
                  fetchClient();
                  setShowCreateProjectModal(false);
                }}
                className="px-4 py-2 bg-green-600 text-white rounded"
              >
                Crear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
 

  return (
    <div className="space-y-6 max-w-4xl mx-auto">

      {/* Header del cliente */}
      <div className="bg-white rounded shadow p-4 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Seguimiento del Proyecto</h2>
          <p className="text-gray-600 text-sm">
            Cliente: {clientData.businessName || clientData.name || clientData.email || clientId}
          </p>
          <p className="text-gray-600 text-sm">Email: {clientData.email}</p>
        </div>
        <div className="flex flex-col gap-2 md:flex-row md:items-center">
          {selectedProject && (
            <button
              onClick={() =>
                handleToggleProjectStatus(
                  selectedProject,
                  selectedProject.estado !== 'finalizado'
                )
              }
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 text-xs md:text-sm"
            >
              {selectedProject.estado === 'finalizado'
                ? 'Reabrir proyecto'
                : 'Marcar como finalizado'}
            </button>
          )}
          <button
            onClick={() => setShowAddPhaseModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm flex items-center gap-1"
          >
            ➕ Añadir Fase
          </button>
        </div>
      </div>

      {/* Selector de Proyecto */}
      <div className="bg-white p-4 rounded shadow">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-2">
          <label className="text-sm font-medium text-gray-700 block">
            Proyecto actual
          </label>
          <div className="flex gap-2 items-center">
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input
                type="checkbox"
                checked={showArchivedProjects}
                onChange={(e) => setShowArchivedProjects(e.target.checked)}
              />
              Ver proyectos finalizados
            </label>
            <button
              onClick={() => setShowCreateProjectModal(true)}
              className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 text-xs"
            >
              ➕ Crear nuevo proyecto
            </button>
          </div>
        </div>
        <select
          className="border p-2 rounded text-sm w-full"
          value={selectedProject?.id || ''}
          onChange={(e) => {
            const proj = projects.find((p) => p.id === e.target.value);
            setSelectedProject(proj || null);
            setShowHistory(false);
          }}
          >
          {projectOptions.length === 0 && (
            <option disabled value="">No hay proyectos disponibles</option>
          )}
          {projectOptions.map((project) => (
            <option key={project.id} value={project.id}>
              {project.projectName || 'Proyecto sin nombre'}
              {project.estado === 'finalizado' ? ' (Finalizado)' : ''}
            </option>
          ))}
        </select>
        {!showArchivedProjects && projects.some((p) => p.estado === 'finalizado') && (
          <p className="text-xs text-gray-500 mt-2">
            Tienes proyectos finalizados ocultos. Marca la casilla para verlos.
          </p>
        )}
      </div>

      {/* Timeline del Proyecto */}
      <div className="bg-white p-4 rounded shadow space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-700">📜 Línea de Tiempo</h3>
          {isArchivedProject && (
            <button
              className="text-xs text-blue-600 hover:underline"
              onClick={() => setShowHistory((prev) => !prev)}
            >
              {showHistory ? 'Ocultar historial' : 'Ver historial completo'}
            </button>
          )}
        </div>
        {!isArchivedProject || showHistory ? (
          <ClientTimeline
            clientData={clientData}
            projectData={selectedProject}
            reloadClient={fetchClient}
          />
        ) : (
          <div className="border rounded p-4 bg-green-50 text-green-800 text-sm">
            <p className="font-semibold">Proyecto finalizado</p>
            <p>
              Este proyecto se marcó como completado el{' '}
              {selectedProject.archivedAt?.toDate?.().toLocaleString?.('es-PR') ||
                '—'}
              . Haz clic en “Ver historial completo” para revisar cada evento.
            </p>
          </div>
        )}

      </div>

      <div className="bg-white rounded shadow p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-gray-800">Actividad reciente del cliente</h3>
          {clientTimelineLoading && (
            <span className="text-xs text-gray-500">Cargando…</span>
          )}
        </div>
        {clientTimelineEntries.length === 0 ? (
          <p className="text-sm text-gray-500">Sin movimientos registrados.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {clientTimelineEntries.map((entry) => (
              <li key={entry.id} className="py-3">
                <p className="text-sm font-semibold text-gray-900">{entry.summary}</p>
                {entry.detail && <p className="text-sm text-gray-600">{entry.detail}</p>}
                <p className="text-xs text-gray-400 mt-1">
                  {entry.createdBy || entry.entityType} ·{' '}
                  {entry.createdAt?.toDate
                    ? entry.createdAt
                        .toDate()
                        .toLocaleString('es-PR', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                    : ''}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Fases del Proyecto */}
      <div className="space-y-4">
        {isArchivedProject && !showHistory && (
          <div className="bg-white p-4 rounded shadow text-sm text-gray-600">
            <p className="font-semibold text-gray-800 mb-1">
              Resumen del proyecto finalizado
            </p>
            <p>
              {selectedProject.projectPhases?.length || 0} fases completadas. Usa el
              botón “Ver historial completo” para revisar detalles o “Reabrir
              proyecto” para continuar trabajando.
            </p>
          </div>
        )}
        {selectedProject.projectPhases?.length === 0 ? (
          <p className="text-gray-500">Este proyecto no tiene fases asignadas.</p>
        ) : (
          selectedProject.projectPhases.map((phase, idx) => {
            const deadlineValue = normalizeDateValue(phase.deadline);
            const startValue = normalizeDateValue(phase.startDate);
            const isDelayed =
              deadlineValue &&
              deadlineValue < new Date() &&
              phase.status !== 'completado';

            const phaseColor = isDelayed
              ? 'border-red-500 bg-red-50'
              : phase.status === 'completado'
                ? 'border-green-500 bg-green-50'
                : 'border-yellow-500 bg-yellow-50';

            return (
              <div key={idx} className={`p-4 rounded border-l-4 ${phaseColor} shadow-sm`}>
                <div className="flex justify-between items-center mb-2">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-700">{phase.name}</h3>
                    <p className="text-sm text-gray-500">
                      Estado: {isDelayed ? 'Atrasado' : phase.status}
                    </p>
                  </div>
                  {!isArchivedProject && (
                    <button
                      onClick={() => openPhaseModal(idx, phase)}
                      className="text-sm text-blue-600 hover:underline"
                    >
                      ✏️ Editar Fase
                    </button>
                  )}
                </div>
                <div className="text-sm text-gray-600 space-y-1">
                  {startValue && (
                    <p>📅 Inicio: {startValue.toLocaleString('es-PR')}</p>
                  )}
                  {deadlineValue && (
                    <p>⏰ Deadline: {deadlineValue.toLocaleString('es-PR')}</p>
                  )}
                  {phase.notes && <p>📝 Notas: {phase.notes}</p>}
                  {phase.subTasks?.length > 0 && (
                    <div className="pt-1">
                      <p className="text-xs font-semibold text-gray-500 uppercase">Asignaciones</p>
                      <ul className="mt-1 space-y-1">
                        {phase.subTasks.map((task) => (
                          <li key={task.id} className="flex items-center gap-2">
                            <span
                              className={`inline-flex h-3 w-3 rounded-full ${
                                task.done ? 'bg-green-500' : 'bg-gray-300'
                              }`}
                            />
                            <span className={`text-sm ${task.done ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                              {task.title}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {phase.requiresPayment && (
                    <p className="flex items-center gap-1">
                      {phase.paymentDone ? (
                        <HiOutlineCheckCircle className="text-green-500" />
                      ) : (
                        <HiOutlineExclamationTriangle className="text-yellow-500" />
                      )}
                      {phase.paymentDone
                        ? `Pago recibido ($${phase.invoiceAmount?.toFixed(2)})`
                        : `Falta pago ($${phase.invoiceAmount?.toFixed(2)})`}
                    </p>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
              {/* Modal: Añadir Fase */}
{showAddPhaseModal && (
  <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center">
    <div className="bg-white rounded p-6 w-full max-w-md space-y-4 shadow">
      <h3 className="text-lg font-semibold">Nueva Fase</h3>

      <div>
        <label className="block text-sm font-medium text-gray-700">Nombre</label>
        <input
          type="text"
          value={newPhase.name}
          onChange={(e) => setNewPhase({ ...newPhase, name: e.target.value })}
          className="w-full border rounded p-2 text-sm"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Estado</label>
        <select
          value={newPhase.status}
          onChange={(e) => setNewPhase({ ...newPhase, status: e.target.value })}
          className="w-full border rounded p-2 text-sm"
        >
          <option value="pendiente">Pendiente</option>
          <option value="en_proceso">En Proceso</option>
          <option value="completado">Completado</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Fecha de inicio</label>
          <input
            type="datetime-local"
            value={newPhase.startDate || ''}
            onChange={(e) => setNewPhase({ ...newPhase, startDate: e.target.value })}
            className="w-full border rounded p-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Fecha límite</label>
          <input
            type="datetime-local"
            value={newPhase.deadline || ''}
            onChange={(e) => setNewPhase({ ...newPhase, deadline: e.target.value })}
            className="w-full border rounded p-2 text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Notas</label>
        <textarea
          className="w-full border rounded p-2 text-sm"
          rows={3}
          value={newPhase.notes}
          onChange={(e) => setNewPhase({ ...newPhase, notes: e.target.value })}
          placeholder="Ej: El cliente necesita enviar el logo..."
        />
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={newPhase.requiresPayment}
          onChange={(e) =>
            setNewPhase({ ...newPhase, requiresPayment: e.target.checked })
          }
        />
        <label className="text-sm text-gray-700">Requiere pago</label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Asignaciones</label>
        <div className="flex gap-2 mt-2">
          <input
            type="text"
            value={newSubTask}
            onChange={(e) => setNewSubTask(e.target.value)}
            className="flex-1 border rounded p-2 text-sm"
            placeholder="Ej: Armar mockups"
          />
          <button
            type="button"
            onClick={handleNewPhaseSubTaskAdd}
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded"
          >
            Añadir
          </button>
        </div>
        {(newPhase.subTasks || []).length > 0 && (
          <ul className="mt-3 space-y-2">
            {newPhase.subTasks.map((task, index) => (
              <li
                key={task.id}
                className="flex items-center justify-between text-sm border rounded p-2"
              >
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={task.done}
                    onChange={() => handleNewPhaseSubTaskToggle(index)}
                  />
                  <span className={task.done ? 'line-through text-gray-400' : ''}>
                    {task.title}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => handleNewPhaseSubTaskRemove(index)}
                  className="text-xs text-red-500 hover:underline"
                >
                  Quitar
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          onClick={() => {
            setShowAddPhaseModal(false);
            setNewPhase({
              name: '',
              status: 'en_proceso',
              requiresPayment: false,
              notes: '',
              startDate: '',
              deadline: '',
              subTasks: [],
            });
            setNewSubTask('');
          }}
          className="px-4 py-2 text-sm bg-gray-200 rounded"
        >
          Cancelar
        </button>
        <button
          onClick={handleAddPhase}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded"
        >
          Guardar
        </button>
      </div>
    </div>
  </div>
)}

{/* Modal: Editar fechas */}
{showModal && phaseForm && (
  <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center">
    <div className="bg-white rounded p-6 w-full max-w-2xl space-y-4 shadow-lg max-h-[90vh] overflow-y-auto">
      <h3 className="text-lg font-semibold">Editar Fase</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Nombre</label>
          <input
            type="text"
            value={phaseForm.name}
            onChange={(e) => setPhaseForm((prev) => ({ ...prev, name: e.target.value }))}
            className="w-full border rounded p-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Estado</label>
          <select
            value={phaseForm.status}
            onChange={(e) => setPhaseForm((prev) => ({ ...prev, status: e.target.value }))}
            className="w-full border rounded p-2 text-sm"
          >
            <option value="pendiente">Pendiente</option>
            <option value="en_proceso">En Proceso</option>
            <option value="completado">Completado</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Fecha de inicio</label>
          <input
            type="datetime-local"
            value={phaseForm.startDate || ''}
            onChange={(e) => setPhaseForm((prev) => ({ ...prev, startDate: e.target.value }))}
            className="w-full border rounded p-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Fecha límite</label>
          <input
            type="datetime-local"
            value={phaseForm.deadline || ''}
            onChange={(e) => setPhaseForm((prev) => ({ ...prev, deadline: e.target.value }))}
            className="w-full border rounded p-2 text-sm"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Notas</label>
        <textarea
          className="w-full border rounded p-2 text-sm"
          rows={3}
          value={phaseForm.notes}
          onChange={(e) => setPhaseForm((prev) => ({ ...prev, notes: e.target.value }))}
          placeholder="Comentarios y observaciones"
        />
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          checked={phaseForm.requiresPayment}
          onChange={(e) =>
            setPhaseForm((prev) => ({ ...prev, requiresPayment: e.target.checked }))
          }
        />
        <label className="text-sm text-gray-700">Requiere pago</label>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Asignaciones</label>
        <div className="flex gap-2 mt-2">
          <input
            type="text"
            value={phaseSubTask}
            onChange={(e) => setPhaseSubTask(e.target.value)}
            className="flex-1 border rounded p-2 text-sm"
            placeholder="Ej: Modal de productos"
          />
          <button
            type="button"
            onClick={handlePhaseSubTaskAdd}
            className="px-3 py-2 text-sm bg-blue-600 text-white rounded"
          >
            Añadir
          </button>
        </div>
        {(phaseForm.subTasks || []).length > 0 && (
          <ul className="mt-3 space-y-2">
            {phaseForm.subTasks.map((task, index) => (
              <li
                key={task.id}
                className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 border rounded p-2 text-sm"
              >
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="checkbox"
                    checked={task.done}
                    onChange={() => handlePhaseSubTaskToggle(index)}
                  />
                  <input
                    type="text"
                    value={task.title}
                    onChange={(e) => handlePhaseSubTaskTitle(index, e.target.value)}
                    className="flex-1 border rounded p-1"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => handlePhaseSubTaskRemove(index)}
                  className="text-xs text-red-500 hover:underline self-start md:self-auto"
                >
                  Quitar
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          onClick={() => {
            setShowModal(false);
            setPhaseForm(null);
            setEditingPhase(null);
            setPhaseSubTask('');
          }}
          className="px-4 py-2 text-sm bg-gray-200 rounded"
        >
          Cancelar
        </button>
        <button
          onClick={handleSavePhase}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded"
        >
          Guardar cambios
        </button>
      </div>
    </div>
  </div>
)}

{showCreateProjectModal && (
  <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex justify-center items-center">
    <div className="bg-white rounded p-6 w-full max-w-md space-y-4 shadow">
      <h3 className="text-lg font-semibold">Nuevo Proyecto</h3>

      {/* Nombre del proyecto */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Nombre del Proyecto
        </label>
        <input
          type="text"
          value={newProject.projectName}
          onChange={(e) =>
            setNewProject({ ...newProject, projectName: e.target.value })
          }
          className="w-full border rounded p-2 text-sm"
          placeholder="Ej: Rediseño web"
        />
      </div>

      {/* ✅ Selector de factura (CORREGIDO) */}
      <div>
        <label className="block text-sm font-medium text-gray-700">
          Asociar a una factura (opcional)
        </label>

        <select
          className="w-full border rounded p-2 text-sm"
          value={newProject.invoiceId}
          onChange={(e) =>
            setNewProject({ ...newProject, invoiceId: e.target.value })
          }
        >
          <option value="">-- Ninguna factura --</option>

          {invoices.length === 0 ? (
            <option disabled>No hay facturas</option>
          ) : (
            invoices.map((invoice) => (
              <option key={invoice.id} value={invoice.id}>
                Factura #{invoice.id} — ${invoice.total ?? 0}
              </option>
            ))
          )}
        </select>
      </div>

      {/* BOTONES */}
      <div className="flex justify-end gap-2 pt-2">
        <button
          onClick={() => setShowCreateProjectModal(false)}
          className="px-4 py-2 text-sm bg-gray-200 rounded"
        >
          Cancelar
        </button>

        <button
          onClick={async () => {
            if (!effectiveClientId) return;
            const clientRef = doc(db, "clients", effectiveClientId);
            const newProjectRef = doc(collection(clientRef, "projects"));

            const projectData = {
              projectName: newProject.projectName || "Proyecto sin nombre",
              invoiceId: newProject.invoiceId || null,
              projectPhases: [],
              projectTimeline: [
                {
                  title: "🚀 Proyecto creado",
                  description: `El proyecto "${newProject.projectName}" ha sido creado.`,
                  color: "blue",
                  timestamp: Timestamp.now(),
                },
              ],
              estado: "activo",
              createdAt: Timestamp.now(),
            };

            await setDoc(newProjectRef, projectData);

            setSelectedProject({
              id: newProjectRef.id,
              ...projectData,
            });

            setNewProject({ projectName: "", invoiceId: "" });
            setShowCreateProjectModal(false);
            fetchClient(); // recarga proyectos
          }}
          className="px-4 py-2 text-sm bg-green-600 text-white rounded"
        >
          Crear Proyecto
        </button>
      </div>
    </div>
  </div>
)}

                </div> 
              );
            };

export default ClientTracking;
